// Mirrored in applications/platform-app/components/web-analytics — change both.
import type { WebDim, WebFilter, Kpis } from './types';

export const RUBY = '#DC143C';
export const RUBY_DEEP = '#9F1239';
export const RUBY_TINT = '#FFF1F3';

export const fmt = (n: number | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e4) return `${(n / 1e3).toFixed(1)}k`;
  return Math.round(n).toLocaleString('en-US');
};
export const money = (cents: number) => `$${Math.round((cents || 0) / 100).toLocaleString('en-US')}`;
/** Tables and lists: dollars AND cents (KPI tiles use whole dollars via `money`). */
export const moneyCents = (cents: number) => `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const pct = (rate: number, digits = 1) => `${((rate || 0) * 100).toFixed(digits)}%`;
export const duration = (sec: number) => {
  const s = Math.max(0, Math.round(sec || 0));
  return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
};

/** Dims the lake can filter on (athena.ts DIM_COLUMNS). Others are mapped or not clickable. */
export const FILTER_DIMS: readonly WebDim[] = ['page', 'entry', 'source', 'channel', 'referrer', 'campaign', 'country', 'city', 'device', 'browser', 'os', 'visitorType', 'product', 'search'];
/** A breakdown row of `dim` filters the dashboard on this dim (exit / 404 rows are paths → page). */
export const filterDimFor = (dim: WebDim): WebDim | null =>
  dim === 'exit' || dim === 'notfound' ? 'page' : FILTER_DIMS.includes(dim) ? dim : null;

export const DIM_LABEL: Record<string, string> = {
  page: 'Page', entry: 'Entry page', exit: 'Exit page', source: 'Source', channel: 'Channel', referrer: 'Referrer',
  campaign: 'Campaign', country: 'Country', city: 'City', device: 'Device', browser: 'Browser', os: 'OS',
  search: 'Search', notfound: 'Broken link', product: 'Product', visitorType: 'Visitor', site: 'Website',
};

let regionNames: Intl.DisplayNames | null | undefined;
export function countryName(code: string): string {
  if (!/^[A-Z]{2}$/.test(code)) return code;
  if (regionNames === undefined) {
    try { regionNames = new Intl.DisplayNames(['en'], { type: 'region' }); } catch { regionNames = null; }
  }
  try { return regionNames?.of(code) || code; } catch { return code; }
}

/** How a dimension value reads on screen (the filter keeps the raw value). */
export function valueLabel(dim: string, value: string): string {
  if (value === '' || value === undefined || value === null) return dim === 'referrer' || dim === 'source' ? '(direct)' : '(none)';
  if (dim === 'country') return countryName(value);
  if (dim === 'visitorType') return value === 'new' ? 'New' : value === 'returning' ? 'Returning' : value;
  return value;
}

export const filterLabel = (f: WebFilter) => `${DIM_LABEL[f.dim] || f.dim}: ${valueLabel(f.dim, f.value)}`;
export const sameFilter = (a: WebFilter, b: WebFilter) => a.dim === b.dim && a.value === b.value;

/** KPI tile id = timeseries metric (readRollups METRICS). */
export type MetricId = 'visitors' | 'pageviews' | 'avgVisitSec' | 'bounceRate' | 'conversionRate' | 'revenueCents' | 'signups';
export const METRIC_NAME: Record<MetricId, string> = {
  visitors: 'Visitors', pageviews: 'Page views', avgVisitSec: 'Avg. visit', bounceRate: 'Bounce rate',
  conversionRate: 'Conversion rate', revenueCents: 'Online revenue', signups: 'Sign-ups',
};
export const METRIC_IDS = Object.keys(METRIC_NAME) as MetricId[];
export function formatMetric(m: MetricId, v: number): string {
  switch (m) {
    case 'avgVisitSec': return duration(v);
    case 'bounceRate': case 'conversionRate': return pct(v, m === 'conversionRate' ? 2 : 1);
    case 'revenueCents': return money(v);
    default: return fmt(v);
  }
}
export const kpiValue = (k: Kpis, m: MetricId): number => (k as any)[m] ?? 0;

/** Chart axis label for a timeseries bucket ('HH:MM' | 'YYYY-MM-DD' | 'YYYY-MM'). */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function bucketLabel(label: string): string {
  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(label);
  if (m) return `${MONTHS[+m[2] - 1]} ${+m[3]}`;
  m = /^(\d{4})-(\d{2})$/.exec(label);
  if (m) return MONTHS[+m[2] - 1];
  return label;
}

export function ago(iso: string | number | undefined, now = Date.now()): string {
  if (iso === undefined || iso === '') return '—';
  const t = typeof iso === 'number' ? iso : Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return s <= 5 ? 'now' : `${s} s ago`;
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
}

export const INCIDENT_LABEL: Record<string, string> = {
  card_testing: 'Card testing', credential_stuffing: 'Credential stuffing', admin_login: 'Admin login attempts',
  path_probing: 'Path probing', scraper: 'Scraper', traffic_spike: 'Traffic spike',
};
export const BLOCK_KIND_LABEL: Record<string, string> = { ip: 'IP', net24: 'IP range', country: 'Country', bot: 'Bot' };

/** Deterministic favicon colour for a site. */
const PALETTE = ['#0f766e', '#1d4ed8', '#b45309', '#7c2d12', '#334155', '#4d7c0f', '#0e7490'];
export function siteColor(siteId: string): string {
  if (siteId === '_all') return '#000';
  if (siteId === 'rubytill') return RUBY;
  let h = 0;
  for (const c of siteId) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length];
}
